import { type Money, addMoney, moneyFromMajorFloat, moneyFromWire, zeroMoney } from "@performyard/money";
import type { ValueGetterParams } from "@performyard/py-components";

import type { DecisionRow, DecisionRowApprovalSnapshot, MyDecisionsDirectReportRow } from "../../types";
import type { DecisionTableContext, IncreaseValues } from "../MyDecisions/types";

export const coerceBudgetIncreaseNumber = (v: number | "" | null | undefined): number => {
	if (v === "" || v === null || v === undefined) return 0;
	return typeof v === "number" && Number.isFinite(v) ? v : 0;
};

export const isDraftPercentMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const p = increaseValues[rowId]?.percent;
	return p === undefined || p === null || p === "";
};

export const isDraftAmountMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const a = increaseValues[rowId]?.amount;
	return a === undefined || a === null || a === "";
};

/** PY-13924: increases must be positive — negatives are flagged, not silently clamped. */
export const isDraftPercentNegative = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const p = increaseValues[rowId]?.percent;
	return typeof p === "number" && p < 0;
};

export const isDraftAmountNegative = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const a = increaseValues[rowId]?.amount;
	return typeof a === "number" && a < 0;
};

export const NEGATIVE_INCREASE_MESSAGE = "Increases must be positive numbers";

export const NOTES_MAX_LENGTH = 800;

/** Prefix for the expanded-notes row that `buildGroupRowDataWithExpansion` injects under its parent row. */
export const SUPER_ROW_ID_PREFIX = "super-";

/** Expanded-notes rows carry no draft of their own, so they sort on the parent's values and stay paired with it. */
const draftLookupId = (row: DecisionRow): string =>
	row.superRow && row._id.startsWith(SUPER_ROW_ID_PREFIX) ? row._id.slice(SUPER_ROW_ID_PREFIX.length) : row._id;

/**
 * Sort key for the New Salary column, mirroring `NewSalaryCellRenderer`: the displayed salary comes from the draft
 * increase, so sorting on the row's server-side `proposed_salary` would order by pre-edit values.
 */
export const newSalaryValueGetter = (params: ValueGetterParams<DecisionRow>): Money | null => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || !context) return null;

	const rowId = draftLookupId(row);
	const increaseAmount = (context.store?.getState().increaseValues[rowId] ?? context.increaseValues[rowId])?.amount;
	if (increaseAmount == null || increaseAmount === "") return null;

	// Money, not a float: the column sorts with `compareMoney`, and this is the
	// same sum `NewSalaryCellRenderer` displays, so the two cannot disagree.
	return addMoney(row.current_salary, moneyFromMajorFloat(increaseAmount));
};

/** Stable string for PATCH `notesByDecisionRowId` keys (direct-report `employee_id`) */
export const notesPayloadKey = (r: DecisionRow): string => String(r.employee_id ?? "");

/**
 * The wire → view-model boundary for money.
 *
 * Money arrives as Extended JSON and becomes `Money` here, exactly once. Nothing
 * downstream sees a float: renderers format, arithmetic uses the money helpers,
 * and the submit path serializes with `moneyToWire`.
 *
 * Totals do not come through here. `money-aggregation.ts` reads the wire values
 * directly, so every sum and budget comparison stays exact.
 */
const mapApprovalToSnapshot = (a: MyDecisionsDirectReportRow["approvals"][number]): DecisionRowApprovalSnapshot => ({
	approver_id: String(a.approverId),
	approver_name: a.approverName,
	sequence: a.sequence,
	status: a.status,
	decided_at: a.decidedAt != null ? String(a.decidedAt) : null,
	notes: a.notes ?? null,
	amount_at_approval: moneyFromWire(a.amountAtApproval),
	updated_at: a.updatedAt != null ? String(a.updatedAt) : null,
	proposed_increase_amount: moneyFromWire(a.proposedIncreaseAmount),
	proposed_increase_percent: a.proposedIncreasePercent ?? null,
	proposed_salary: moneyFromWire(a.proposedSalary),
});

export const toDecisionRow = (
	dr: MyDecisionsDirectReportRow,
	index: number,
	userID: string | undefined
): DecisionRow => {
	const approvalEntry =
		userID === undefined || dr.approvals.length === 0
			? null
			: (dr.approvals.find((a) => String(a.approverId) === userID) ?? null);

	const approvalEntries = dr.approvals.map(mapApprovalToSnapshot);

	return {
		_id: String(dr.id),
		compensation_cycle_id: String(dr.compensationCycle),
		employee_id: String(dr.employeeId),
		employee_name: dr.name || `Employee ${index + 1}`,
		employee_title: dr.jobTitle,
		employee_role: dr.role?.name ?? null,
		current_salary: moneyFromWire(dr.currentSalary) ?? zeroMoney(),
		proposed_increase_percent: dr.proposedIncreasePercent,
		proposed_increase_amount: moneyFromWire(dr.proposedIncreaseAmount),
		proposed_salary: moneyFromWire(dr.proposedSalary),
		status: dr.status,
		notes: dr.myDecisionNotes ?? approvalEntry?.notes ?? null,
		approver: approvalEntry?.approverId != null ? String(approvalEntry.approverId) : null,
		updatedAt: dr.updatedAt ?? approvalEntry?.updatedAt ?? null,
		approval_entries: approvalEntries.length > 0 ? approvalEntries : undefined,
	};
};
